import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookmarkIcon, XMarkIcon, CheckIcon, CheckCircleIcon, TruckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { razorpayService } from '../../services/razorpayService';
import firebaseService from '../../services/firebaseService';

const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all bg-gray-50";
const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

const HardCopyModal = ({ isOpen, onClose }) => {
  const { user, setPurchaseStatus } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '', phone: user?.phone || '', whatsappNumber: user?.phone || '',
    address: '', city: '', state: '', pincode: '', landmark: '', alternateContact: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    if (!user) return;
    setIsProcessing(true);
    try {
      const deliveryAddress = {
        name: formData.name, address: formData.address, city: formData.city,
        state: formData.state, pincode: formData.pincode, landmark: formData.landmark,
        alternateContact: formData.alternateContact
      };
      const orderResponse = await razorpayService.createOrder(1499, 'INR', null, {
        product: 'hard_copy', userId: user.uid, userName: user.name || user.phone,
        contactNumber: formData.phone, whatsappNumber: formData.whatsappNumber, deliveryAddress
      });
      if (!orderResponse.success) throw new Error('Failed to create order');

      const paymentResponse = await razorpayService.initializeRazorpayCheckout(
        orderResponse.order.id, 1499, 'INR',
        { description: 'Fijoli Hard Copy Book', customerName: formData.name, customerPhone: formData.whatsappNumber, themeColor: '#059669' }
      );

      const verificationResponse = await razorpayService.verifyPayment(
        paymentResponse.razorpay_order_id, paymentResponse.razorpay_payment_id, paymentResponse.razorpay_signature
      );

      if (verificationResponse.success) {
        await firebaseService.saveHardCopyOrder({
          userId: user.uid, userName: user.name || user.phone, userEmail: user.email,
          product: 'hard_copy', amount: 1499, currency: 'INR',
          razorpayOrderId: orderResponse.order.id, razorpayPaymentId: paymentResponse.razorpay_payment_id,
          whatsappNumber: formData.whatsappNumber, orderType: 'hard_copy',
          deliveryAddress, status: 'completed', paymentAmount: 1499
        });
        const accessType = user.hasPurchased ? 'both' : 'hard_copy';
        await setPurchaseStatus(true, accessType, 1499);
        setOrderSuccess(true);
        setTimeout(() => handleClose(), 2000);
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (error) {
      alert('Order failed: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setOrderSuccess(false);
    setFormData({
      name: user?.name || '', phone: user?.phone || '', whatsappNumber: user?.phone || '',
      address: '', city: '', state: '', pincode: '', landmark: '', alternateContact: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        />
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60, scale: 0.97 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className="relative w-full sm:max-w-lg bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden"
          style={{ maxHeight: '92vh', overflowY: 'auto' }}
        >
          {/* Header */}
          <div className="relative px-6 pt-7 pb-5 text-white"
            style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
            <button onClick={handleClose}
              className="absolute top-4 right-4 p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors focus:outline-none"
              aria-label="Close">
              <XMarkIcon className="h-5 w-5 text-white" />
            </button>
            <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mb-5 sm:hidden" />
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                <BookmarkIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold leading-tight">Hard Copy Book</h2>
                <p className="text-xs text-green-200">Delivered to your doorstep</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 py-5">
            {!orderSuccess ? (
              <>
                {/* Pricing */}
                <div className="flex items-center gap-3 mb-5 p-4 bg-green-50 rounded-2xl border border-green-100">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-green-600">₹1499</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Free shipping · 7–10 business days</p>
                  </div>
                  <div className="ml-auto flex flex-col items-end gap-1 text-xs text-green-700">
                    {['Physical book delivery', 'Premium quality printing', 'Free shipping', 'Track your order'].map(f => (
                      <span key={f} className="flex items-center gap-1">
                        <CheckIcon className="h-3 w-3 text-green-500" /> {f}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleOrder} className="space-y-4">
                  {/* Section: Personal */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Personal Info</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                          required placeholder="Your full name" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Contact Number <span className="text-red-500">*</span></label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                          required placeholder="For delivery calls" className={inputClass} />
                      </div>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div>
                    <label className={labelClass}>WhatsApp Number <span className="text-red-500">*</span></label>
                    <input type="tel" name="whatsappNumber" value={formData.whatsappNumber} onChange={handleInputChange}
                      required placeholder="For order updates via WhatsApp" className={inputClass} />
                    <p className="text-xs text-gray-400 mt-1.5">Delivery tracking & updates sent here</p>
                  </div>

                  {/* Delivery address */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Delivery Address</p>
                    <div className="space-y-3">
                      <div>
                        <label className={labelClass}>Complete Address <span className="text-red-500">*</span></label>
                        <textarea name="address" value={formData.address} onChange={handleInputChange}
                          required rows="3" placeholder="House/Flat no., Street, Area, Colony"
                          className={inputClass + ' resize-none'} />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className={labelClass}>City <span className="text-red-500">*</span></label>
                          <input type="text" name="city" value={formData.city} onChange={handleInputChange}
                            required placeholder="City" className={inputClass} />
                        </div>
                        <div>
                          <label className={labelClass}>State <span className="text-red-500">*</span></label>
                          <input type="text" name="state" value={formData.state} onChange={handleInputChange}
                            required placeholder="State" className={inputClass} />
                        </div>
                        <div>
                          <label className={labelClass}>Pincode <span className="text-red-500">*</span></label>
                          <input type="text" name="pincode" value={formData.pincode} onChange={handleInputChange}
                            required placeholder="6-digit" className={inputClass} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={labelClass}>Landmark <span className="text-gray-400 font-normal">(optional)</span></label>
                          <input type="text" name="landmark" value={formData.landmark} onChange={handleInputChange}
                            placeholder="Near hospital, school…" className={inputClass} />
                        </div>
                        <div>
                          <label className={labelClass}>Alternate Contact <span className="text-gray-400 font-normal">(optional)</span></label>
                          <input type="tel" name="alternateContact" value={formData.alternateContact} onChange={handleInputChange}
                            placeholder="Family member's number" className={inputClass} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <button type="submit" disabled={isProcessing || !user}
                    className={`w-full py-3.5 px-6 rounded-2xl font-semibold text-base transition-all ${isProcessing || !user
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg active:scale-[0.98]'
                      }`}>
                    {isProcessing ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : 'Place Order — ₹1499'}
                  </button>

                  <p className="text-center text-xs text-gray-400">🔒 Secure payment via Razorpay</p>
                </form>
              </>
            ) : (
              /* Success */
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="h-9 w-9 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Order Placed!</h3>
                <p className="text-sm text-gray-500 mb-5">We'll start processing your order right away.</p>
                <div className="space-y-3 text-left">
                  <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
                    <p className="text-sm font-medium text-green-800">📱 WhatsApp updates enabled</p>
                    <p className="text-xs text-green-500 mt-1">Order updates sent to {formData.whatsappNumber}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-center gap-3">
                    <TruckIcon className="h-5 w-5 text-blue-500 shrink-0" />
                    <p className="text-sm font-medium text-blue-800">Estimated delivery: 7–10 business days</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default HardCopyModal;
